import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { apiService } from 'services/base.service';
import PageText from 'components/base/page-text';
import { MealCategoryInfo, MealCategoryType, MealType } from 'utils/types';
import { Breadcrumbs, Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import OrganizeItem from 'components/base/organize-item';
import DeleteModal from 'components/base/delete-modal';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import Meal from './meal';
import { ROUTE_KEYS } from 'utils/constants';

const CategoryMeals = () => {
  const { category_id } = useParams();

  const [loading, setLoading] = useState(false);
  const [mealList, setMealList] = useState<MealType[]>([]);
  const [prevMealList, setPrevMealList] = useState<MealType[]>([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [activeMeal, setActiveMeal] = useState<MealType>({} as MealType);
  const [categoryInfo, setCategoryInfo] = useState<MealCategoryInfo>();
  const { brand } = categoryInfo || {};
  const [step, setStep] = useState(0);
  const [showDeleteMealModal, setshowDeleteMealModal] = useState(false);
  const [dragAndDropActive, setDragAndDropActive] = useState(false);
  const isTrash = new URLSearchParams(useLocation().search).get('trash') === 'true';

  const deleteMeal = () => {
    setshowDeleteMealModal(!showDeleteMealModal);
  };

  const breadCrumbs = [
    {
      name: brand ? `View ${brand.name}` : '...',
      path: `${ROUTE_KEYS.MEALS}?brand_id=${brand?.id}`
    },
    {
      name: 'Meals',
      path: ''
    }
  ];
  const fetchData = async () => {
    if (!category_id) return;
    try {
      setLoading(true);
      const response = await apiService.get(`marketing/meal-categories/meals/${category_id}`, {
        isInternal: true
      });
      const data = response.data;
      let meals = data;
      if (isTrash) {
        meals = data?.meals?.filter((item: MealCategoryType) => item?.is_deleted);
      }
      setCategoryInfo(data?.meal_category);
      setMealList(meals);
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over, collisions } = event;

    if (!collisions?.length) return;
    if (active.id === over?.id) return;

    setMealList((meals) => {
      const oldIndex = meals.findIndex((meal) => meal.id === active.id);
      const newIndex = meals.findIndex((meal) => meal.id === over?.id);
      return arrayMove(meals, oldIndex, newIndex);
    });
    setStep((prev) => prev + 1);
  };

  const cancelUpdate = () => {
    setMealList(prevMealList);
    setStep(0);
    setDragAndDropActive(false);
  };

  const updatePosition = async (items: MealType[]) => {
    setUpdateLoading(true);
    const meal_ids = items.map((item) => item?.id);
    const payload = {
      meals: meal_ids,
      meal_category_id: items[0]?.meal_category_id
    };
    try {
      const res = await apiService.put(`marketing/meal/position`, { data: payload });
      if (res) {
        setStep(0);
        setDragAndDropActive(false);
      }
    } catch (error: any) {
      setMealList(prevMealList);
      toast(<ToastMessage message={error.response.data.message} type='error' />);
    } finally {
      setUpdateLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (step === 0 && mealList) setPrevMealList(JSON.parse(JSON.stringify(mealList)));
  }, [step, mealList]);
  const handleDeleteClick = async () => {
    try {
      setLoading(true);

      await apiService.delete(`marketing/meal/${activeMeal?.id}`);
      toast(<ToastMessage type='success' message='meal archived successfully' />);
      fetchData();
      setshowDeleteMealModal(false);
    } catch {
      toast(<ToastMessage type='error' message='Error occur while deleting meal' />);
    } finally {
      setLoading(false);
    }
  };
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  );
  const onDeleteMeal = (meal_id: string) => {
    setMealList(mealList.filter((meal) => meal.id !== meal_id));
  };

  return (
    <div>
      <div className='flex flex-wrap items-start justify-between mb-6'>
        {!isTrash && <Breadcrumbs breadcrumbs={breadCrumbs} />}
        {!isTrash && (
          <div>
            {!dragAndDropActive ? (
              <OrganizeItem text='meals' onClick={() => setDragAndDropActive(true)} />
            ) : (
              <div className='gap-3 flex items-center'>
                <Button
                  small
                  className='flex justify-end h-10 w-[100px] ml-auto'
                  variant='outline'
                  color='primary'
                  disabled={updateLoading}
                  onClick={cancelUpdate}>
                  Cancel
                </Button>

                <Button
                  small
                  className='flex justify-end h-10 w-[100px] ml-auto'
                  variant='block'
                  color='primary'
                  loading={updateLoading}
                  disabled={
                    updateLoading || JSON.stringify(mealList) === JSON.stringify(prevMealList)
                  }
                  onClick={() => updatePosition(mealList ?? [])}>
                  Save
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {loading ? (
        <PageText text='loading' />
      ) : mealList?.length === 0 ? (
        <PageText text='No meals found' />
      ) : (
        <div className='grid md:grid-cols-3 lg:grid-cols-4 gap-6'>
          {dragAndDropActive ? (
            <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd} sensors={sensors}>
              <SortableContext items={mealList}>
                {mealList?.map((meal) => {
                  return (
                    <Meal
                      key={meal.id}
                      meal={meal}
                      activeMeal={activeMeal}
                      setActiveMeal={setActiveMeal}
                      onDeleteMeal={onDeleteMeal}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          ) : (
            mealList?.map((meal) => {
              return (
                <Meal
                  key={meal.id}
                  meal={meal}
                  activeMeal={activeMeal}
                  setActiveMeal={setActiveMeal}
                  onDeleteMeal={onDeleteMeal}
                />
              );
            })
          )}
        </div>
      )}

      <DeleteModal
        show={showDeleteMealModal}
        onClose={deleteMeal}
        handleDelete={() => handleDeleteClick()}
        title={`Are you sure you want to archive ${activeMeal?.name} ?`}
        description='This action cannot be undone'
        loading={loading}
      />
    </div>
  );
};

export default CategoryMeals;
